import { Button } from "./Button";
import { Icon } from "./Icon";
import type { IconGlyph } from "@/icons";
import { cn } from "@/lib/cn";
import { SPRING } from "@/lib/motion";
import { motion } from "motion/react";
import { forwardRef, useId, type KeyboardEvent, type ReactElement, type ReactNode, type Ref } from "react";

/** One segment of a SegmentedControl. */
export type SegmentOption<T extends string> = { value: T; label: ReactNode; icon?: IconGlyph };

export type SegmentedControlProps<T extends string> = {
  options: readonly SegmentOption<T>[];
  value: T;
  onValueChange: (value: T) => void;
  /** Accessible name of the tablist. */
  label: string;
  size?: "sm" | "md";
  fullWidth?: boolean;
  /** Prefix for tab ids and `aria-controls`; panels use `segmentPanelId(prefix, value)`. */
  idPrefix?: string;
  className?: string;
};

/** Id of the panel a segment controls, so pages can label their panels to match. */
export function segmentPanelId(prefix: string, value: string): string {
  return `${prefix}-panel-${value}`;
}

const SEGMENT_SIZE = { sm: "px-3 py-2.5 text-sm", md: "px-4 py-4 text-sm" } as const;

function SegmentedControlInner<T extends string>(
  { options, value, onValueChange, label, size = "md", fullWidth = true, idPrefix, className }: SegmentedControlProps<T>,
  ref: Ref<HTMLDivElement>,
) {
  const thumbId = useId();

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const index = options.findIndex((option) => option.value === value);
    const last = options.length - 1;
    const target =
      event.key === "ArrowRight" ? (index + 1) % options.length
      : event.key === "ArrowLeft" ? (index - 1 + options.length) % options.length
      : event.key === "Home" ? 0
      : event.key === "End" ? last
      : null;
    if (target === null) return;
    event.preventDefault();
    onValueChange(options[target].value);
    event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]')[target]?.focus();
  }

  return (
    <div
      ref={ref}
      role="tablist"
      aria-label={label}
      onKeyDown={handleKeyDown}
      className={cn(
        "gap-1 rounded-control border border-border bg-card p-1.5",
        fullWidth ? "flex w-full" : "inline-flex",
        className,
      )}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <Button
            key={option.value}
            variant="plain"
            tone="neutral"
            size="none"
            role="tab"
            id={idPrefix ? `${idPrefix}-tab-${option.value}` : undefined}
            aria-selected={active}
            aria-controls={idPrefix ? segmentPanelId(idPrefix, option.value) : undefined}
            tabIndex={active ? 0 : -1}
            selected={active}
            onClick={() => onValueChange(option.value)}
            className={cn(
              "relative font-semibold",
              fullWidth && "flex-1",
              SEGMENT_SIZE[size],
              active ? "text-primary-foreground hover:text-primary-foreground" : "text-card-text hover:text-card-text",
            )}
          >
            {active && (
              <motion.span
                layoutId={thumbId}
                aria-hidden
                transition={SPRING.thumb}
                className="absolute inset-0 rounded-control bg-primary"
              />
            )}
            <span
              className={cn(
                "relative inline-flex items-center gap-2 transition-transform duration-base ease-standard",
                active ? "scale-110" : "scale-100",
              )}
            >
              {option.icon ? <Icon as={option.icon} size="sm" /> : null}
              {option.label}
            </span>
          </Button>
        );
      })}
    </div>
  );
}

/** Tablist of 2-4 mutually exclusive views with an animated thumb; arrows, Home and End move the selection. */
export const SegmentedControl = forwardRef(SegmentedControlInner) as <T extends string>(
  props: SegmentedControlProps<T> & { ref?: Ref<HTMLDivElement> },
) => ReactElement;
